import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Crown, Check, ArrowLeft, Lock, Zap, MessageSquare, Bell, CheckCircle2, Gift, RefreshCw, Smartphone, Star, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { isIapAvailable, purchaseTier, restorePurchases, getTierPackage } from "@/lib/iap";
import { shopPlanAccess, PLAN_LOCKED_BUTTON_LABEL } from "@/lib/shopPurchaseGate";
import { isNative, platform } from "@/lib/native";
import { TIERS, displayPriceForTier } from "@/lib/tiers";

const FEATURES_FREE = [
  "1 active lead at a time",
  "Submit 1 active quote",
  "Message owners on leads you've quoted",
  "Standard profile listing",
];

const FEATURES_PRO = [
  { icon: Lock, text: "Unlock 3 concurrent leads in your radius" },
  { icon: Zap, text: "Submit up to 3 active quotes" },
  { icon: MessageSquare, text: "Message owners on any unlocked lead" },
  { icon: Star, text: "Pro badge on your profile" },
];

const FEATURES_PREMIUM = [
  { icon: Lock, text: "Unlock every lead in your radius — no cap" },
  { icon: Zap, text: "Submit unlimited concurrent quotes" },
  { icon: MessageSquare, text: "Message any owner before quoting" },
  { icon: Bell, text: "Priority placement in owner search results" },
  { icon: Crown, text: "Premium badge on your profile" },
];

export default function Upgrade() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const safeBack = () => window.history.length > 2 ? navigate(-1) : navigate("/shop", { replace: true });
  const [params, setParams] = useSearchParams();
  const [subState, setSubState] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [subscribeLoading, setSubscribeLoading] = useState(null);
  const [jobCount, setJobCount] = useState(null);
  const [iapAvailable, setIapAvailable] = useState(false);
  const [iapPackages, setIapPackages] = useState({ pro: null, premium: null });
  const [restoring, setRestoring] = useState(false);
  // The checkout and copy share the backend's canonical billing_config.py value.
  const [trialDays, setTrialDays] = useState(null);
  useEffect(() => {
    api.get("/config").then(r => setTrialDays(r.data.trial_days)).catch(() => setTrialDays(null));
  }, []);
  const trialLabel = trialDays ? `${trialDays}-day free trial` : "free trial";
  const currentTier = user?.subscription_tier || "free";
  const isPaid = currentTier === "pro" || currentTier === "premium";
  const trialUntil = user?.welcome_trial_until;
  const premiumUntil = user?.premium_until;
  const isOnTrial = !!(isPaid && trialUntil && premiumUntil && new Date(premiumUntil).getTime() <= new Date(trialUntil).getTime() + 60_000);
  const trialDaysLeft = isOnTrial && trialUntil ? Math.max(0, Math.ceil((new Date(trialUntil).getTime() - Date.now()) / 86_400_000)) : 0;
  const trialEligible = !!user?.trial_eligible;
  const isFoundingMember = !!user?.is_founding_member;
  const foundingDaysLeft = user?.founding_days_remaining || 0;
  const isActiveFoundingMember = isFoundingMember && foundingDaysLeft > 0;
  const wasFoundingMember = !!user?.was_founding_member;
  // Founding shops (active or expired) never get the 30-day trial offer.
  // They either have founding access or go straight to paid pricing.
  const effectiveTrialEligible = trialEligible && !wasFoundingMember;
  // shop-status-fixes-nov2 (H1): pending / rejected / suspended shops can't
  // buy or restore a plan (they would be charged for access they can't use).
  const planGate = shopPlanAccess(user);
  const planLocked = !planGate.allowed;
  const subscriptionParam = params.get("subscription");
  const cancelled = params.get("subscription") === "cancelled" || params.get("cancelled");

  const refreshSubState = useCallback(() => {
    return api.get("/billing/subscription").then((res) => setSubState(res.data)).catch(() => setSubState(null));
  }, []);
  useEffect(() => { refreshSubState(); }, [refreshSubState]);

  useEffect(() => {
    if (user?.role !== "shop") return;
    api.get("/shop/nearby-job-count").then((r) => setJobCount(r.data)).catch(() => setJobCount(null));
  }, [user]);

  useEffect(() => {
    let cancelledLocal = false;
    (async () => {
      const avail = isIapAvailable();
      if (!avail) { setIapAvailable(false); return; }
      const proPkg = await getTierPackage("pro");
      const premiumPkg = await getTierPackage("premium");
      if (!cancelledLocal) {
        setIapAvailable(!!(proPkg || premiumPkg));
        setIapPackages({ pro: proPkg, premium: premiumPkg });
      }
    })();
    return () => { cancelledLocal = true; };
  }, []);

  const onSubscribeNative = async (tier) => {
    if (planLocked) {
      toast.error(planGate.message);
      return;
    }
    setSubscribeLoading(tier);
    try {
      const r = await purchaseTier(tier, { user });
      if (r.ok) {
        toast.success(`Welcome to ${tier === "premium" ? "Premium" : "Pro"} 🎉`);
        await checkAuth();
        await refreshSubState();
      } else if (r.userCancelled) {
      } else if (r.syncRefused || r.blocked) {
        toast.error(r.error);
        await checkAuth();
      } else {
        toast.error(r.error || "Purchase failed");
      }
    } finally {
      setSubscribeLoading(null);
    }
  };

  const onRestore = async () => {
    if (planLocked) {
      toast.error(planGate.message);
      return;
    }
    setRestoring(true);
    try {
      const r = await restorePurchases({ user });
      if (r.syncRefused || r.blocked) {
        toast.error(r.error);
        await checkAuth();
      } else if (r.ok && r.restored) {
        toast.success("Subscription restored — welcome back");
        await checkAuth();
        await refreshSubState();
      } else if (r.ok) {
        toast.info("No previous subscription found for this account.");
      } else {
        toast.error(r.error || "Restore failed");
      }
    } finally {
      setRestoring(false);
    }
  };

  const onSubscribeRecurring = async (tier, claimTrial = false) => {
    setSubscribeLoading(tier);
    try {
      const res = await api.post("/billing/subscription/checkout", {
        origin_url: window.location.origin,
        tier,
        claim_trial: !!claimTrial,
      });
      if (res.data?.already_subscribed) {
        toast.success("Plan upgraded successfully!");
        window.location.href = "/dashboard";
        return;
      }
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("No checkout URL returned");
        setSubscribeLoading(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start subscription checkout");
      setSubscribeLoading(null);
    }
  };

  const onOpenPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post("/billing/portal", { origin_url: window.location.origin });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Portal unavailable");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open billing portal");
    } finally {
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    if (cancelled) {
      toast.info("Checkout cancelled. No charge was made.");
      setParams({}, { replace: true });
      return;
    }
    if (subscriptionParam === "success") {
      toast.success("Subscription active 🎉 — give us a few seconds while Stripe finishes…");
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        await Promise.all([checkAuth(), refreshSubState()]);
        if (attempts >= 6) {
          setParams({}, { replace: true });
          return;
        }
        setTimeout(poll, 1500);
      };
      poll();
    }
  }, [subscriptionParam, cancelled, checkAuth, refreshSubState, setParams]);

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      <main className="max-w-[1200px] mx-auto px-4 sm:px-8 lg:px-12 py-12" data-testid="upgrade-page">
        <button onClick={() => safeBack()} className="text-zinc-500 hover:text-white text-sm flex items-center gap-1 mb-8" data-testid="upgrade-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mb-12">
          <div className="label-eyebrow text-[#FF3B30] mb-3 flex items-center gap-2"><Crown className="w-3 h-3" /> SHIFTQUOTE PLANS</div>
          <h1 className="font-heading text-5xl sm:text-6xl font-black tracking-tighter mb-4">More leads.<br /><span className="text-[#FF3B30]">More revenue.</span></h1>
          <p className="text-zinc-400 text-lg max-w-2xl">Free shops see one lead at a time. Pro unlocks three. Premium unlocks every nearby lead — no cap.</p>
          {isPaid && user?.premium_until && (
            <div className={`mt-4 inline-flex items-center gap-2 border text-xs uppercase tracking-widest px-3 py-2 ${isOnTrial ? "border-[#FF9500]/40 bg-[#FF9500]/5 text-[#FF9500]" : "border-[#34C759]/40 bg-[#34C759]/5 text-[#34C759]"}`} data-testid="paid-active-banner">
              {isOnTrial ? (
                <><Gift className="w-3 h-3" /> Free trial · {trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left · ends {new Date(user.premium_until).toLocaleDateString()}</>
              ) : (
                <><CheckCircle2 className="w-3 h-3" /> {currentTier === "premium" ? "Premium" : "Pro"} active until {new Date(user.premium_until).toLocaleDateString()}</>
              )}
            </div>
          )}
        </div>

        {planLocked && (
          <div className="border border-[#FF9500]/60 bg-gradient-to-r from-[#FF9500]/20 to-[#FF9500]/0 px-5 py-4 mb-6 flex flex-wrap items-center gap-3" data-testid="plans-after-approval-notice">
            <Lock className="w-5 h-5 text-[#FF9500] shrink-0" />
            <div className="flex-1 text-sm">
              <span className="text-white font-semibold">{planGate.level === "pending" || !planGate.level ? "Plans unlock after approval." : "Plans are unavailable."}</span>
              <span className="text-zinc-200"> {planGate.message}</span>
            </div>
          </div>
        )}

        {isOnTrial && (
          <div className="border border-[#FF9500]/40 bg-gradient-to-r from-[#FF9500]/15 to-[#FF9500]/0 px-5 py-4 mb-6 flex flex-wrap items-center gap-3" data-testid="welcome-trial-banner">
            <Gift className="w-5 h-5 text-[#FF9500] shrink-0" />
            <div className="flex-1 text-sm">
              <span className="text-white font-semibold">You're on your {trialLabel}.</span>
              <span className="text-zinc-200"> Every nearby lead is unlocked. You'll be charged {displayPriceForTier(currentTier, iapPackages[currentTier])} when the trial ends — cancel any time before then.</span>
            </div>
          </div>
        )}

        {effectiveTrialEligible && !isPaid && (
          <div className="border border-[#FF9500]/40 bg-gradient-to-r from-[#FF9500]/15 to-[#FF9500]/0 px-5 py-4 mb-6 flex flex-wrap items-center gap-3" data-testid="trial-eligible-banner">
            <Gift className="w-5 h-5 text-[#FF9500] shrink-0" />
            <div className="flex-1 text-sm">
              <span className="text-white font-semibold">{trialLabel} available.</span>
              <span className="text-zinc-300"> Pick Pro or Premium below — add a payment method, no charge until the trial ends, cancel anytime before then.</span>
            </div>
          </div>
        )}

        {subState?.status === "past_due" && subState?.decline_reason && (
          <div className="border border-[#FF3B30]/40 bg-gradient-to-r from-[#FF3B30]/15 to-[#FF3B30]/0 px-5 py-4 mb-6 flex flex-wrap items-center gap-3" data-testid="payment-declined-banner">
            <AlertTriangle className="w-5 h-5 text-[#FF3B30] shrink-0" />
            <div className="flex-1 text-sm">
              <span className="text-white font-semibold">Your last payment was declined.</span>
              <span className="text-zinc-200"> Reason: {subState.decline_reason}.</span>
              <span className="text-zinc-300"> Please update your payment method to keep your subscription active.</span>
            </div>
            <button
              onClick={onOpenPortal}
              disabled={portalLoading}
              className="shrink-0 bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
            >
              {portalLoading ? "Opening…" : "Update payment"}
            </button>
          </div>
        )}

        {isActiveFoundingMember && (
          <div className="mb-6 rounded-xl border border-green-700/50 bg-green-950/40 p-4 text-center">
            <p className="text-lg font-bold text-green-400">🎁 You're a Founding Shop</p>
            <p className="mt-1 text-sm text-green-300">
              You already have full <span className="font-semibold">Premium access</span> — {foundingDaysLeft} day{foundingDaysLeft !== 1 ? "s" : ""} remaining in your founding period.
            </p>
            <p className="mt-1 text-xs text-zinc-400">Paid plans unlock automatically once your 120-day founding period ends.</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          <div className="bg-[#121214] p-8">
            <div className="flex items-baseline justify-between mb-1">
              <h3 className="font-heading text-2xl font-bold">Free</h3>
              {currentTier === "free" && <div className="label-eyebrow text-zinc-500">CURRENT</div>}
            </div>
            <div className="font-heading text-5xl font-black tracking-tighter mb-6">$0<span className="text-zinc-500 text-base font-normal ml-1">/month</span></div>
            <ul className="space-y-3 mb-8">
              {FEATURES_FREE.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                  <Check className="w-4 h-4 text-zinc-600 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            {jobCount && jobCount.total > 0 && jobCount.tier === "free" ? (
              <div className="border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-3 py-2.5 mt-2 text-xs text-zinc-200 font-mono" data-testid="free-card-lead-counter">
                You're seeing <span className="text-white font-bold">{jobCount.visible}</span> of <span className="text-[#FF3B30] font-bold">{jobCount.total}</span> nearby lead{jobCount.total === 1 ? "" : "s"} right now.
                {jobCount.total > 1 && <span className="text-zinc-500"> {jobCount.total - jobCount.visible} hidden →</span>}
              </div>
            ) : (
              <div className="h-10" />
            )}
          </div>

          <TierCard
            tier="pro"
            title="Pro"
            color="#FF9500"
            icon={Star}
            price={displayPriceForTier("pro", iapPackages.pro)}
            badge={currentTier === "pro" ? "Active" : null}
            features={FEATURES_PRO}
            currentTier={currentTier}
            isOnTrial={isOnTrial}
            trialDaysLeft={trialDaysLeft}
            trialEligible={effectiveTrialEligible}
            trialDays={trialDays}
            isFoundingMember={isFoundingMember}
            foundingDaysLeft={foundingDaysLeft}
            subState={subState}
            iapAvailable={iapAvailable}
            iapPackage={iapPackages.pro}
            subscribeLoading={subscribeLoading}
            onSubscribeNative={() => onSubscribeNative("pro")}
            onSubscribeRecurring={(claimTrial) => onSubscribeRecurring("pro", claimTrial)}
            onOpenPortal={onOpenPortal}
            portalLoading={portalLoading}
            onRestore={onRestore}
            restoring={restoring}
            planLocked={planLocked}
          />

          <TierCard
            tier="premium"
            title="Premium"
            color="#FF3B30"
            icon={Crown}
            price={displayPriceForTier("premium", iapPackages.premium)}
            badge={currentTier === "premium" ? "Active" : "Recommended"}
            features={FEATURES_PREMIUM}
            currentTier={currentTier}
            isOnTrial={isOnTrial}
            trialDaysLeft={trialDaysLeft}
            trialEligible={effectiveTrialEligible}
            trialDays={trialDays}
            isFoundingMember={isFoundingMember}
            foundingDaysLeft={foundingDaysLeft}
            subState={subState}
            iapAvailable={iapAvailable}
            iapPackage={iapPackages.premium}
            subscribeLoading={subscribeLoading}
            onSubscribeNative={() => onSubscribeNative("premium")}
            onSubscribeRecurring={(claimTrial) => onSubscribeRecurring("premium", claimTrial)}
            onOpenPortal={onOpenPortal}
            portalLoading={portalLoading}
            onRestore={onRestore}
            restoring={restoring}
            planLocked={planLocked}
            highlighted
          />
        </div>

        {iapAvailable && (
          <button
            onClick={onRestore}
            disabled={restoring || planLocked}
            className="btn-secondary mt-6 mx-auto flex items-center justify-center gap-2"
            data-testid="restore-purchases-btn"
          >
            <RefreshCw className={`w-4 h-4 ${restoring ? "animate-spin" : ""}`} />
            {restoring ? "Restoring…" : "Restore purchases"}
          </button>
        )}

        <div className="mt-10 text-xs text-zinc-500 text-center max-w-xl mx-auto">
          Secure checkout & billing by Stripe on web, by {platform() === "ios" ? "Apple" : "Google Play"} on mobile. Cancel anytime — you keep access until the end of your billing period.
        </div>
      </main>
    </div>
  );
}

function TierCard({
  tier, title, color, icon: Icon, price, badge, features, currentTier, isOnTrial,
  trialDaysLeft, trialEligible, trialDays, subState, iapAvailable, iapPackage, subscribeLoading,
  onSubscribeNative, onSubscribeRecurring, onOpenPortal, portalLoading, highlighted,
  isFoundingMember, foundingDaysLeft, planLocked,
}) {
  const isActiveFoundingMember = isFoundingMember && foundingDaysLeft > 0;
  const isCurrent = currentTier === tier;
  const subActive = !!subState?.stripe_subscription_id && subState?.tier === tier;
  const loading = subscribeLoading === tier;
  const fineprint = trialEligible
    ? `Card required · ${trialDays ? `No charge for ${trialDays} days` : "No charge during the trial"} · Auto-renews at ${price}/mo · Cancel anytime`
    : null;

  const ctaLabel = (() => {
    if (subActive) return "Subscription active";
    if (isCurrent) return "Current plan";
    if (trialEligible) return `Start ${trialDays ? `${trialDays}-day ` : ""}free trial`;
    return `Subscribe — ${price}/mo`;
  })();

  return (
    <div
      className={`bg-[#121214] p-8 relative ${highlighted ? `ring-2 -m-px` : ""}`}
      style={highlighted ? { "--tw-ring-color": `${color}66` } : {}}
      data-testid={`tier-card-${tier}`}
    >
      {badge && (
        <div className="absolute top-0 right-0 text-white text-[10px] uppercase tracking-widest px-3 py-1" style={{ background: color }}>
          {badge}
        </div>
      )}
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="font-heading text-2xl font-bold flex items-center gap-2">
          <Icon className="w-5 h-5" style={{ color }} /> {title}
        </h3>
      </div>
      <div className="font-heading text-5xl font-black tracking-tighter mb-1">{price}<span className="text-zinc-500 text-base font-normal ml-1">/month</span></div>
      <div className="text-[11px] text-zinc-500 mb-6">{trialEligible ? `${trialDays ? `Free for ${trialDays} days` : "Free during the trial"}, then ${price}/mo. Cancel anytime.` : `${price}/mo. Cancel anytime.`}</div>
      <ul className="space-y-3 mb-6">
        {features.map((f) => {
          const FIcon = f.icon;
          return (
            <li key={f.text} className="flex items-start gap-2 text-sm text-zinc-100">
              <FIcon className="w-4 h-4 mt-0.5 shrink-0" style={{ color }} />
              <span>{f.text}</span>
            </li>
          );
        })}
      </ul>

      {!subActive && !isCurrent && (
        <div className="flex items-center justify-center gap-1.5 text-[11px] text-zinc-400 mb-3 font-mono">
          <Check className="w-3 h-3 text-[#34C759]" strokeWidth={3} />
          Cancel anytime — no commitment
        </div>
      )}

      {planLocked && !subActive ? (
        <button
          disabled
          className="mt-4 w-full cursor-not-allowed rounded-lg border border-[#FF9500]/60 bg-[#FF9500]/10 py-3 text-sm font-semibold text-[#FF9500]"
          data-testid={`plan-locked-${tier}-btn`}
        >
          <Lock className="inline w-4 h-4 mr-1 -mt-0.5" /> {PLAN_LOCKED_BUTTON_LABEL}
        </button>
      ) : (isOnTrial || isActiveFoundingMember) ? (
        <button
          disabled
          className="mt-4 w-full cursor-not-allowed rounded-lg bg-zinc-800 py-3 text-sm font-semibold text-zinc-500 opacity-60"
        >
          {isActiveFoundingMember
            ? `🔒 Founding access — ${foundingDaysLeft} day${foundingDaysLeft !== 1 ? "s" : ""} left`
            : `🔒 Available in ${trialDaysLeft} day${trialDaysLeft !== 1 ? "s" : ""}`}
        </button>
      ) : iapAvailable ? (
        <button
          onClick={onSubscribeNative}
          disabled={loading || !iapPackage || subActive}
          className="btn-primary w-full flex items-center justify-center gap-2"
          data-testid={`subscribe-iap-${tier}-btn`}
          style={highlighted ? { background: color } : {}}
        >
          <Smartphone className={`w-4 h-4 ${loading ? "animate-pulse" : ""}`} />
          {loading
            ? "Opening Store…"
            : subActive
              ? `${title} active`
              : !iapPackage
                ? "Not available"
                : `Subscribe — ${iapPackage?.product?.priceString || price + "/mo"}`}
        </button>
      ) : isNative() ? (
        <div className="border border-white/10 bg-white/5 p-4 text-center" data-testid={`subscribe-gated-${tier}`}>
          <Lock className="w-5 h-5 text-zinc-400 mx-auto mb-2" />
          <div className="text-xs uppercase tracking-widest text-zinc-400 mb-1">Coming soon to mobile</div>
          {/* p0-security-nov2: no link / URL to web payment inside the native
              app (Google Play + App Store billing rules). Web keeps Stripe. */}
          <div className="text-xs text-zinc-500" data-testid={`subscribe-gated-note-${tier}`}>
            In-app subscriptions are on the way. Your current plan keeps working in the meantime.
          </div>
        </div>
      ) : (
        <>
          <button
            onClick={() => onSubscribeRecurring(trialEligible)}
            disabled={loading || subActive || isCurrent}
            className="btn-primary w-full flex items-center justify-center gap-2"
            style={highlighted ? { background: color } : {}}
            data-testid={trialEligible ? `start-trial-${tier}-btn` : `subscribe-recurring-${tier}-btn`}
          >
            {trialEligible ? <Gift className={`w-4 h-4 ${loading ? "animate-pulse" : ""}`} /> : <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />}
            {loading ? "Opening Stripe…" : ctaLabel}
          </button>

          {trialEligible && !subActive && (
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2 font-mono text-center" data-testid={`trial-fineprint-${tier}`}>
              {fineprint}
            </div>
          )}

          {subActive && (
            <button
              onClick={onOpenPortal}
              disabled={portalLoading}
              className="btn-secondary w-full mt-3 flex items-center justify-center gap-2"
              data-testid={`manage-subscription-${tier}-btn`}
            >
              <RefreshCw className={`w-4 h-4 ${portalLoading ? "animate-spin" : ""}`} />
              {portalLoading ? "Opening portal…" : "Manage subscription"}
            </button>
          )}
          {subActive && subState?.status && (
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-3 font-mono text-center">
              Stripe · <span className={subState.status === "active" || subState.status === "trialing" ? "text-[#34C759]" : subState.status === "past_due" ? "text-[#FF9500]" : "text-zinc-400"}>{subState.status}</span>
              {subState.cancel_at_period_end ? " · cancels at period end" : ""}
            </div>
          )}
        </>
      )}
    </div>
  );
}
